// Dwellow grid insights: public embeddable widget.
//
// Reads Britain's electricity grid "out loud", a live plain-English verdict on
// how clean the grid is right now, historic trends, and a celebratory record
// book. The UI is the Dwellow Design System component (vendor/dwellowGrid.js +
// vendor/widget.css); this controller wires it to live data:
//   - the "Now" view runs client-side against the keyless, CORS-enabled NESO
//     Carbon Intensity API;
//   - Trends and Records load a small precomputed JSON from the Dwellow API
//     (materialised nightly from Elexon + NESO), with bundled fallbacks.
//
// No framework: the design component is vanilla, so unlike the other journeys
// this embed does not load Alpine.
//
// Mount: <div data-journey="grid-insights"
//             data-direction="warm"                 (warm | compact | playful)
//             data-tab="now"                         (now | trends | records)
//             data-api-base="https://api.dwellow.co.uk"
//             data-quote-url="https://www.dwellow.co.uk/heat-pumps/quote"></div>

import widgetCss from './vendor/widget.css?raw';
import { DwellowGrid } from './vendor/dwellowGrid.js';
import { createCarbonIntensityClient } from './carbonIntensityApi.js';
import { buildNow, buildTrends, buildRecords } from './buildData.js';
import { FALLBACK_NOW, FALLBACK_RECORDS, FALLBACK_HISTORY } from './fallback.js';

const DEFAULT_API_BASE = 'https://api.dwellow.co.uk';
const DEFAULT_QUOTE_URL = 'https://www.dwellow.co.uk/heat-pumps/quote';
const DEFAULT_SOLAR_URL = 'https://www.dwellow.co.uk/solar/quote';
const DEFAULT_SHARE_URL = 'https://www.dwellow.co.uk';
const REFRESH_MS = 5 * 60 * 1000;

// Small outer chrome around the design widget: the gentle product bridge and the
// required data-source attribution. Kept separate from the vendored widget CSS.
// NOTE: DwellowGrid.mount() sets the host element's className to "dw-grid" (it
// drops the "gi-host" class), so widget-internal enhancements below are scoped to
// ".dw-grid", not ".gi-host". Only the outer chrome (.gi-wrap / .gi-cta / .gi-attr)
// lives on elements we control directly.
const CHROME_CSS = `
/* the whole embed (widget + CTA + attribution) shares one centred, capped column
   so the CTA mirrors the widget width exactly */
.gi-wrap { max-width: 760px; margin: 0 auto; }
/* the hand-rolled chart SVGs carry a pixel width attribute; keep that from ever
   forcing the widget wider than its container on narrow screens */
.dw-grid .dw-chart-slot { min-width: 0; overflow: hidden; cursor: crosshair; }
.dw-grid .dw-mixbar__seg { cursor: pointer; }
.gi-cta { margin-top: 12px; display: flex; flex-wrap: wrap; align-items: center; gap: 12px 16px;
  justify-content: space-between; background: #0f766e; color: #fff; border-radius: 14px; padding: 14px 18px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; }
.gi-cta p { margin: 0; font-size: 14.5px; line-height: 1.4; flex: 1 1 220px; }
.gi-cta__links { display: flex; flex-wrap: wrap; gap: 8px; flex: none; }
.gi-cta a { background: #fff; color: #0b5a54; font-weight: 700; text-decoration: none;
  padding: 9px 15px; border-radius: 9px; font-size: 14px; white-space: nowrap; }
.gi-cta a:hover { background: #eafaf7; }
.gi-cta a:focus-visible { outline: 3px solid rgba(255,255,255,.6); outline-offset: 2px; }
.gi-attr { margin: 10px 4px 0; font-size: 11.5px; line-height: 1.55; color: #8a8a82;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; }
.gi-attr a { color: #6f6f68; }
/* interactive tooltip for mix bars + trend charts (see vendor/dwellowGrid.js) */
.dw-tip { position: fixed; z-index: 2147483000; pointer-events: none; transform: translate(-50%, -100%);
  background: #17140e; color: #fff; font-size: 12px; line-height: 1.3; padding: 5px 9px; border-radius: 7px;
  white-space: nowrap; box-shadow: 0 4px 14px rgba(23,20,14,.28);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; }
.dw-tip[hidden] { display: none; }
.dw-tip b { font-weight: 700; }
.dw-grid .dw-otd__date { font-weight: 400; color: var(--dw-muted); font-size: 0.85em; margin-left: 4px; }
/* share panel: the card keeps its 1.9:1 preview shape; the action buttons live
   BELOW it so three real buttons can never be clipped inside the fixed ratio */
.dw-grid .dw-share-panel { width: 100%; max-width: 340px; display: flex; flex-direction: column; gap: 12px; }
.dw-grid .dw-share-panel .dw-share-card { max-width: 100%; }
.dw-grid .dw-share-actions { margin-top: 0; flex-wrap: wrap; justify-content: center; }
.dw-grid .dw-btn { display: inline-flex; align-items: center; }
.dw-grid .dw-btn svg { width: 14px; height: 14px; margin-right: 6px; flex: none; }
/* loading skeleton — shown until live data is in, so fallback values never flash */
.dw-grid[aria-busy="true"] .dw-tab { pointer-events: none; opacity: .55; }
.dw-grid .gi-skel { display: grid; gap: 12px; padding: 4px 0 8px; }
.dw-grid .gi-skel__grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px 16px; }
.dw-grid .gi-skel__line, .dw-grid .gi-skel__bar, .dw-grid .gi-skel__chart {
  border-radius: 8px;
  background: linear-gradient(90deg, rgba(23,20,14,.055) 25%, rgba(23,20,14,.11) 37%, rgba(23,20,14,.055) 63%);
  background-size: 400% 100%; animation: gi-shimmer 1.4s ease infinite; }
.dw-grid .gi-skel__line { height: 14px; }
.dw-grid .gi-skel__bar { height: 30px; }
.dw-grid .gi-skel__chart { height: 130px; }
@keyframes gi-shimmer { from { background-position: 100% 0; } to { background-position: 0 0; } }
@media (prefers-reduced-motion: reduce) {
  .dw-grid .gi-skel__line, .dw-grid .gi-skel__bar, .dw-grid .gi-skel__chart { animation: none; }
}
`;

const ATTRIBUTION =
    'Carbon intensity &amp; live mix from the <a href="https://carbonintensity.org.uk" target="_blank" rel="noopener">NESO Carbon Intensity API</a> (CC BY 4.0). ' +
    'Records &amp; history from the NESO Data Portal and Elexon (Contains BMRS data © Elexon). Great Britain only. Not affiliated with or endorsed by NESO.';

function attributionHtml() {
    return ATTRIBUTION + ' © ' + new Date().getFullYear() + ' Dwellow Limited.';
}

function bridgeCopy(band, both) {
    if (band === 'clean') {
        return 'The grid is clean right now, and your own solar or heat pump would be lapping up this cheap, green power.';
    }
    return both
        ? 'Want your home to run on more of the clean stuff? See what a heat pump or solar could do.'
        : 'Want your home to run on more of the clean stuff? See what you could save.';
}

function injectStyles() {
    if (document.getElementById('dw-grid-styles')) return;
    const s1 = document.createElement('style');
    s1.id = 'dw-grid-styles';
    s1.textContent = widgetCss;
    document.head.appendChild(s1);
    const s2 = document.createElement('style');
    s2.id = 'gi-chrome-styles';
    s2.textContent = CHROME_CSS;
    document.head.appendChild(s2);
}

function currentViewOf(host) {
    const active = host.querySelector('.dw-tab[aria-selected="true"]');
    const id = active?.getAttribute('id') || '';
    const view = id.split('-tab-')[1];
    return ['now', 'trends', 'records'].includes(view) ? view : null;
}

// A loading placeholder that mirrors the widget's own header/tabs, with a shimmer
// body — so we can wait for live data instead of flashing the bundled fallback.
function skeletonHtml() {
    const icon = DwellowGrid.icon;
    const tab = (ic, label, on) => `<button class="dw-tab"${on ? ' aria-selected="true"' : ''} tabindex="-1">${icon(ic)}<span>${label}</span></button>`;
    const line = (w, h) => `<div class="gi-skel__line" style="width:${w}${h ? ';height:' + h : ''}"></div>`;
    return '' +
        '<div class="dw-head"><div class="dw-head__title"><span class="dw-head__mark">' + icon('zap') + '</span>' +
          '<span class="dw-head__label"><span class="dw-head__kicker">Britain’s electricity</span>' +
          '<span class="dw-head__name">Live from the grid</span></span></div>' +
          '<span class="dw-head__updated"><span class="dw-live-dot"></span>Updating…</span></div>' +
        '<div class="dw-tabs">' + tab('zap', 'Now', true) + tab('chart', 'Trends') + tab('trophy', 'Records') + '</div>' +
        '<div class="dw-tabs-underline"></div>' +
        '<div class="dw-body"><div class="gi-skel" role="status" aria-label="Loading Britain’s grid">' +
          line('70%', '26px') + line('92%') + line('58%') +
          '<div class="gi-skel__bar"></div>' +
          '<div class="gi-skel__bar" style="height:52px"></div>' +
          line('45%') +
          '<div class="gi-skel__bar"></div>' +
          '<div class="gi-skel__grid">' + line('40%') + line('40%') + line('40%') + line('40%') + '</div>' +
          '<div class="gi-skel__chart"></div>' +
        '</div></div>';
}

function createController(root) {
    const ds = root.dataset || {};
    const config = {
        // data-api-base="" explicitly disables the records/history fetch (use the
        // bundled fallback, no cross-origin requests) — handy for local preview.
        apiBase: ds.apiBase === '' ? '' : (ds.apiBase || ds.baseUrl || DEFAULT_API_BASE),
        quoteUrl: ds.quoteUrl === '' ? '' : (ds.quoteUrl || DEFAULT_QUOTE_URL),
        solarUrl: ds.solarUrl === '' ? '' : (ds.solarUrl || DEFAULT_SOLAR_URL),
        shareUrl: ds.shareUrl || DEFAULT_SHARE_URL,
        direction: ['warm', 'compact', 'playful'].includes(ds.direction) ? ds.direction : 'warm',
        initialView: ['now', 'trends', 'records'].includes(ds.tab) ? ds.tab : 'now',
    };
    const hasCta = Boolean(config.quoteUrl || config.solarUrl);
    const api = createCarbonIntensityClient();

    // Build the outer chrome; the widget mounts into .gi-host.
    root.classList.add('gi-wrap');
    root.innerHTML =
        '<div class="gi-host"></div>' +
        (hasCta ? '<div class="gi-cta"></div>' : '') +
        '<p class="gi-attr">' + attributionHtml() + '</p>';
    let host = root.querySelector('.gi-host');
    const ctaEl = root.querySelector('.gi-cta');

    // Start from fallback (instant, correctly-shaped) then upgrade to live.
    const data = {
        now: buildNow({ intensity: FALLBACK_NOW.intensity, mix: FALLBACK_NOW.mix }),
        trends: buildTrends(FALLBACK_HISTORY),
        records: buildRecords(FALLBACK_RECORDS, { liveNow: {}, currentYearG: null }),
        shareUrl: config.shareUrl,
    };

    function paintCta() {
        if (!ctaEl) return;
        const both = config.quoteUrl && config.solarUrl;
        const link = (href, label) =>
            '<a href="' + href + '" target="_blank" rel="noopener">' + label + '</a>';
        const links = [];
        if (config.quoteUrl) links.push(link(config.quoteUrl, both ? 'Heat pumps' : 'See what you’d save'));
        if (config.solarUrl) links.push(link(config.solarUrl, both ? 'Solar' : 'See what you’d save'));
        ctaEl.innerHTML =
            '<p>' + bridgeCopy(data.now.band, both) + '</p>' +
            '<span class="gi-cta__links">' + links.join('') + '</span>';
    }

    function render(view) {
        // Replace the host node so old resize observers / listeners drop cleanly.
        const fresh = document.createElement('div');
        fresh.className = 'gi-host';
        host.replaceWith(fresh);
        host = fresh;
        DwellowGrid.mount(host, { direction: config.direction, view, data });
        paintCta();
    }

    function renderSkeleton() {
        const fresh = document.createElement('div');
        fresh.className = 'dw-grid';
        fresh.setAttribute('data-direction', config.direction);
        fresh.setAttribute('lang', 'en-GB');
        fresh.setAttribute('aria-busy', 'true');
        fresh.innerHTML = skeletonHtml();
        host.replaceWith(fresh);
        host = fresh;
        paintCta();
    }

    // Live "Now" from the Carbon Intensity API (keyless, CORS-enabled) — refreshed
    // on the interval because it changes every half-hour.
    async function refreshNow() {
        try {
            const [intensity, mix, todaySeries, forecastSeries] = await Promise.all([
                api.intensityNow(),
                api.generationNow(),
                api.intensityToday().catch(() => []),
                api.intensityForecast24h().catch(() => []),
            ]);
            if (intensity) data.now = buildNow({ intensity, mix, todaySeries, forecastSeries });
        } catch { /* keep fallback now */ }
    }

    // Trends + Records from the Dwellow API — fetched ONCE (nightly data), and
    // skipped entirely when no api base is configured (e.g. local preview), so we
    // never spam the console with CORS errors against an origin that can't read them.
    let endpointsLoaded = false;
    async function loadEndpoints() {
        if (endpointsLoaded || !config.apiBase) return;
        endpointsLoaded = true;
        const [history, records] = await Promise.all([
            fetchJson(config.apiBase, '/grid_history', FALLBACK_HISTORY),
            fetchJson(config.apiBase, '/grid_records', FALLBACK_RECORDS),
        ]);
        data.trends = buildTrends(history);
        const currentYearG = data.trends.yearCarbon.slice(-1)[0]?.g || null;
        data.records = buildRecords(records, {
            liveNow: {
                actual: data.now.gaugeG,
                zeroCarbonShare: zeroCarbonShareOf(data.now.mix),
                gasShare: shareOfDesignMix(data.now.mix, 'gas'),
            },
            currentYearG,
        });
    }

    async function loadLive() {
        await Promise.all([refreshNow(), loadEndpoints()]);
    }

    let painted = false;
    function firstPaint() {
        if (painted) return;
        painted = true;
        render(currentViewOf(host) || config.initialView);
    }

    injectStyles();
    // Show a loading skeleton, then paint real data ONCE — so users never see the
    // bundled fallback numbers swap to live ones. If the network is slow, paint
    // what we have (fallback) after a few seconds rather than spinning forever.
    renderSkeleton();
    const guard = setTimeout(firstPaint, 6000);
    loadLive().finally(() => { clearTimeout(guard); firstPaint(); });

    setInterval(() => {
        // Only refresh once painted and while the reader is on the live "Now" tab,
        // so a periodic re-render never interrupts them mid-interaction elsewhere.
        if (!painted || document.visibilityState === 'hidden') return;
        if (currentViewOf(host) !== 'now') return;
        refreshNow().then(() => render('now'));
    }, REFRESH_MS);
}

function zeroCarbonShareOf(designMix) {
    const z = ['solar', 'wind', 'hydro', 'nuclear'];
    return (designMix || []).filter(f => z.includes(f.id)).reduce((s, f) => s + (Number(f.pct) || 0), 0);
}
function shareOfDesignMix(designMix, id) {
    const f = (designMix || []).find(m => m.id === id);
    return f ? Number(f.pct) || 0 : 0;
}

async function fetchJson(apiBase, path, fallback) {
    try {
        const url = new URL(path, apiBase);
        const res = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
        const body = await res.json().catch(() => null);
        if (!res.ok || !body || body.available === false) return fallback;
        return body;
    } catch {
        return fallback;
    }
}

export function registerGridInsights() {
    if (typeof document === 'undefined') return;

    const mounted = new WeakSet();
    const mount = root => {
        if (!root || mounted.has(root)) return;
        mounted.add(root);
        createController(root);
    };
    const findRoot = () => document.querySelector('[data-journey="grid-insights"]');

    const ready = () => {
        const root = findRoot();
        if (root) { mount(root); return; }
        const observer = new MutationObserver(() => {
            const next = findRoot();
            if (!next) return;
            mount(next);
            observer.disconnect();
        });
        observer.observe(document.documentElement, { childList: true, subtree: true });
        setTimeout(() => observer.disconnect(), 5000);
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', ready);
    } else {
        ready();
    }
}

registerGridInsights();
